/* eslint-disable */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { updateClusterRoles } from '../../request/request';
import { roleTagVariant, networkInterfacesColumns } from './ReflectorsAssociatedData';
import GenericCard from '@ui-components/Card/GenericCard';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericButton from '@ui-components/Button/GenericButton';
import { GenericTable } from '@ui-components/Table/GenericTable';
import { mapInterfaces } from '../../../../../helpers/formattersShapedData';

const KeyValueRows: React.FC<{ items: { label: string, value: React.ReactNode }[] }> = ({ items }) => (
  <div className="flex flex-col divide-y divide-gray-100">
    {items.map(item => (
      <div key={item.label} className="flex justify-between w-full py-1 items-center min-h-[32px]">
        <span className="text-sm text-semantic-text-secondary">{item.label}</span>
        <span className="text-sm text-semantic-text-primary">{item.value}</span>
      </div>
    ))}
  </div>
);

interface ReflectorsAssociatedProps {
  reflectors: any[];
  clusterId?: string | number;
  onRoleChange?: () => void; // Callback to refresh the UI
}



const ReflectorsAssociated: React.FC<ReflectorsAssociatedProps> = ({ reflectors, clusterId, onRoleChange }) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  // Helper function to get the role change button label
  const getRoleChangeButtonLabel = (currentRole: string) => {
    // Only show "Set to Primary" when the reflector is secondary
    if (currentRole && currentRole.toLowerCase() === 'secondary') {
      return 'Set to Primary';
    }
    // Don't show any button for primary reflectors
    return null;
  };

  // Helper function to handle role change
  const handleRoleChange = async (reflectorId: string, currentRole: string) => {
    if (!clusterId) {
      console.error('Cluster ID is required');
      return;
    }

    // Only allow changing from secondary to primary
    if (currentRole && currentRole.toLowerCase() !== 'secondary') {
      console.error('Can only set secondary reflectors to primary');
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      
      // Set the clicked reflector to primary and the other to secondary
      const updatedRoles = reflectors.map(reflector => {
        if (reflector.id === reflectorId) {
          // This is the clicked reflector - set to primary
          return {
            reflectorId: Number(reflector.id),
            role: 'primary'
          };
        } else {
          // This is the other reflector - automatically set to secondary
          return {
            reflectorId: Number(reflector.id),
            role: 'secondary'
          };
        }
      });

      const payload = {
        roles: updatedRoles
      };

      await updateClusterRoles(token, Number(clusterId), payload);
      console.log(`Role changed for reflector ${reflectorId} to primary in cluster ${clusterId}`);
      
      // Refresh the UI to show updated roles
      if (onRoleChange) {
        onRoleChange();
      } else {
        // Fallback: reload the page if no callback is provided
        window.location.reload();
      }
      
    } catch (error) {
      console.error('Error changing role:', error);
      // Handle error, e.g., show a toast notification
    }
  };

  return (
    <div className="space-y-6">
    
      {reflectors.map(reflector => {
        const keyValueItems = [
          { label: 'Last Sync', value: reflector.lastUpdate },
          { label: 'Version', value: reflector.version || '-'},
          { label: 'Cloud', value: <GenericTag label={reflector.data?.cloud?.toUpperCase() || '-'} variant={reflector.data?.cloud === 'aws' ? 'warning' : 'neutral'} size="small" /> },
          { label: 'Region', value: <GenericTag label={reflector.data?.region || '-'} variant="neutral" size="small"  /> },
          { label: 'Role', value: <GenericTag label={reflector.role || '-'} variant={roleTagVariant[reflector.role] as 'success' | 'neutral' | 'default'} size="small" /> },
          { label: 'License Type', value: reflector.license?.type || '-' },
          { label: 'License Serial', value: reflector.license?.serial || '-' },
        ];

        const roleChangeLabel = getRoleChangeButtonLabel(reflector.role);

        return (
          <GenericCard key={reflector.id} isCollapsible={false}>
            <div className="flex flex-col md:flex-row md:items-stretch md:justify-between gap-4 p-4">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-base text-semantic-text-primary">{reflector.description}</span>
                </div>
                <div className="text-xs text-gray-500 mb-2">ID: {reflector.name}</div>
                <KeyValueRows items={keyValueItems} />
              </div>
              <div className="flex-1 min-w-[260px] flex flex-col justify-between pl-2">
                <div>
                  <div className="font-semibold text-sm mb-2">Network Interfaces</div>
                  <div className="overflow-x-auto">
                    <GenericTable
                      columns={networkInterfacesColumns}
                      data={mapInterfaces(reflector.interfaces)}
                      initialRowsPerPage={2}
                      rowsPerPageOptions={[2, 5]}
                      width="100%"
                      tableClassName="bg-white"
                      isFooterVisible={false}
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4 justify-end">
                  {reflector.status === 'Passive' && (
                    <GenericButton label="Make Active" size="sm" variant="secondary" shape="ghost" />
                  )}
                  {roleChangeLabel && (
                    <GenericButton 
                      label={roleChangeLabel} 
                      size="sm" 
                      variant="brand" 
                      shape="filled" 
                      onClick={() => handleRoleChange(reflector.id, reflector.role)}
                    />
                  )}
                  <GenericButton label="View Details" size="sm" variant="secondary" shape="ghost" onClick={() => navigate(`/reflector-v2/management/bgp-reflectors/details/${reflector.id}`)} />
                </div>
              </div>
            </div>
          </GenericCard>
        );
      })}
    </div>
  );
};

export default ReflectorsAssociated; 